import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, ChevronLeft, Activity, FileText, BarChart3, Users, Shield, Cpu, Zap } from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

const ShopMedos = () => {
  const navigate = useNavigate();

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      tagline: 'Essential tools for independent clinics.',
      description: 'Everything you need to run a single-unit practice.',
      accentColor: '#06b6d4',
      chipLabel: 'MEDOS Core',
      features: ['Core Billing & Inventory', 'GST-ready Invoicing', 'Basic Sales Reports', 'Up to 3 Staff'],
      ctaLabel: 'Inquiry',
      isPopular: false,
    },
    {
      id: 'advanced',
      name: 'Advanced',
      tagline: 'AI-driven infrastructure for scaling hospitals.',
      description: 'Full-spectrum management with ZYPHER AI assistant.',
      accentColor: '#06b6d4',
      chipLabel: 'Advanced + ZYPHER',
      features: ['Everything in Starter', 'ZYPHER AI Assistant', 'Advanced Analytics', 'Unlimited Staff', 'Multi-location', 'Priority Support'],
      ctaLabel: 'Inquiry',
      isPopular: true,
    },
    {
      id: 'lifetime',
      name: 'Lifetime',
      tagline: "The Founder's Deal. Perpetual ownership.",
      description: 'One-time purchase. No recurring fees + lifetime updates.',
      accentColor: '#06b6d4',
      chipLabel: 'Lifetime License',
      features: ['Everything in Advanced', 'No Recurring Fees', 'Priority Legacy Support', 'Lifetime Updates'],
      ctaLabel: 'Inquiry',
      isPopular: false,
    },
  ];

  return (
    <div className="store-page bg-white dark:bg-black text-[#1d1d1f] dark:text-[#f5f5f7] transition-colors duration-400 min-h-screen">
      {/* Breadcrumb */}
      <div className="max-w-[1120px] mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <Link to="/shop" className="inline-flex items-center gap-1 text-[#065DC0] text-xs sm:text-sm font-medium hover:underline">
          <ChevronLeft size={14} /> Back to Store
        </Link>
      </div>

      {/* Hero */}
      <section className="py-6 sm:py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 sm:gap-6">
            <div>
              <motion.h1 variants={fadeInUp} className="text-[clamp(28px,6vw,64px)] font-bold tracking-tight leading-none">
                Shop MedOS
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-[#6e6e73] dark:text-[#a1a1a6] text-sm sm:text-base md:text-lg font-light mt-2 sm:mt-3 max-w-lg">
                Choose the plan that fits your practice.
              </motion.p>
            </div>
            <motion.div variants={fadeInUp}>
              <Link to="/products/medos" className="text-[#065DC0] text-xs sm:text-sm font-medium inline-flex items-center gap-1 hover:underline">
                Learn more <ArrowRight size={14} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Product Hero Banner */}
      <section className="py-4 sm:py-6 md:py-10">
        <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative bg-gradient-to-br from-[#1e3a8a] via-[#3b82f6] to-[#7c3aed] dark:from-[#0f172a] dark:via-[#1e40af] dark:to-[#4c1d95] rounded-[20px] sm:rounded-[32px] md:rounded-[44px] p-5 sm:p-8 md:p-14 overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
            <div className="relative z-10 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 md:gap-8">
              <div className="flex-1">
                <span className="text-[10px] sm:text-xs font-bold text-white/60 uppercase tracking-[0.3em] mb-2 sm:mb-3 block">Clinical Software</span>
                <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white tracking-tight mb-2 sm:mb-4">FOZZIL MedOS</h2>
                <p className="text-white/80 text-xs sm:text-sm md:text-base leading-relaxed max-w-md mb-4 sm:mb-6">
                  AI-driven pharmacy and hospital management. Built for Indian healthcare with GST compliance and smart inventory.
                </p>
                <div className="flex gap-2 sm:gap-3 flex-wrap">
                  <button
                    onClick={() => navigate('/products/medos')}
                    className="px-4 sm:px-6 py-2.5 sm:py-3 rounded-full bg-white text-[#0e7490] text-xs sm:text-sm font-bold hover:bg-white/90 transition-all inline-flex items-center gap-1.5 sm:gap-2 cursor-pointer border-none"
                  >
                    Inquiry <ArrowRight size={14} />
                  </button>
                  <Link
                    to="/products/medos"
                    className="px-4 sm:px-6 py-2.5 sm:py-3 rounded-full bg-transparent text-white text-xs sm:text-sm font-semibold border border-white/30 hover:bg-white/10 transition-all inline-flex items-center gap-2"
                  >
                    Explore
                  </Link>
                </div>
              </div>
              <div className="w-[100px] sm:w-[160px] md:w-[220px] shrink-0 aspect-square bg-black rounded-[24px] sm:rounded-[32px] overflow-hidden flex items-center justify-center p-0">
                <img
                  src={process.env.PUBLIC_URL + '/FOZZIL MEDOS LOGO.png'}
                  alt="FOZZIL MedOS"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* All Plans - Ecosystem Card Style Compact */}
              <section className="py-8 sm:py-12 md:py-16">
                <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
                  <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={stagger}>
                    <motion.h2 variants={fadeInUp} className="text-[clamp(20px,3.5vw,36px)] font-bold tracking-tight mb-5 sm:mb-8">
                      All plans. <span className="text-[#065DC0]/80 dark:text-blue-400/80 font-medium">Upgrade your MedOS.</span>
                    </motion.h2>
      
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
                      {plans.map((plan) => (
                          <motion.div key={plan.id} variants={fadeInUp}>
                            <div
                              className={`relative group p-4 sm:p-5 md:p-7 rounded-[20px] sm:rounded-[28px] md:rounded-[32px] bg-white dark:bg-zinc-950 border transition-all duration-700 overflow-hidden h-full flex flex-col ${
                                plan.isPopular
                                  ? 'border-blue-500 shadow-[0_32px_64px_-16px_rgba(6,93,192,0.2)]'
                                  : plan.isComingSoon
                                  ? 'border-dashed border-black/[0.08] dark:border-white/[0.08] opacity-70 hover:opacity-100'
                                  : 'border-black/[0.08] dark:border-white/[0.08] hover:border-blue-500/30 shadow-sm dark:shadow-none'
                              }`}
                            >
                              {/* Artistic Background Elements */}
                              <div className="absolute inset-0 bg-gradient-to-b from-blue-500/[0.02] to-transparent pointer-events-none" />
                              <div className="absolute -top-12 -right-12 w-32 h-32 bg-[#065DC0]/5 blur-[40px] rounded-full pointer-events-none" />
                    {/* Hover Glow */}
                        <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 bg-blue-600/10 blur-[60px] opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />

                        {plan.isPopular && (
                          <div className="absolute top-3 right-3 sm:top-4 sm:right-4 md:top-6 md:right-6 z-30 bg-blue-500/10 text-blue-600 dark:text-blue-400 text-[8px] sm:text-[10px] uppercase font-black px-2 sm:px-3 py-0.5 sm:py-1 rounded-full border border-blue-500/20">Most Popular</div>
                        )}
                        

                    {/* Logo instead of Icon */}
                    <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-[32px] flex items-center justify-center mb-3 sm:mb-4 md:mb-5 bg-black border border-black/[0.06] dark:border-white/[0.1] group-hover:scale-110 transition-transform duration-700 relative overflow-hidden p-0">
                      <div className="absolute inset-0 bg-blue-500/5 dark:bg-blue-400/10 pointer-events-none" />
                      <img src={process.env.PUBLIC_URL + '/FOZZIL MEDOS LOGO.png'} alt="MedOS" className="w-full h-full object-cover shadow-2xl" />
                    </div>

                    <h3 className="text-base sm:text-lg md:text-2xl font-bold tracking-tighter text-[#1d1d1f] dark:text-white mb-0.5 sm:mb-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors uppercase">{plan.name}</h3>
                    <p className="text-[10px] sm:text-xs md:text-sm font-light text-[#6e6e73] dark:text-zinc-500 mb-3 sm:mb-4 line-clamp-2 md:line-clamp-3">{plan.tagline}</p>

                    {/* Badge */}
                    <div className="inline-flex items-center gap-1 sm:gap-1.5 px-2 py-1 rounded-full text-[8px] sm:text-[9px] md:text-[10px] font-semibold bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#6e6e73] dark:text-[#a1a1a6] border border-black/[0.08] dark:border-white/[0.08] mb-3 sm:mb-4 w-fit truncate max-w-full">
                      <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-blue-500 shrink-0" />
                      <span className="truncate">{plan.chipLabel}</span>
                    </div>

                    {/* Features List - compact */}
                    <div className="flex flex-col mb-3 sm:mb-4 md:mb-5 w-full flex-1">
                      {plan.features.map((feat, i) => (
                        <div key={i} className="flex items-center gap-2 sm:gap-3 py-1.5 sm:py-2 md:py-2.5 border-b border-black/[0.04] dark:border-white/5 text-[10px] sm:text-[11px] md:text-[13px]">
                           <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-blue-500 shrink-0" />
                           <span className="font-medium text-[#1d1d1f] dark:text-zinc-300">{feat}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA - Single Line */}
                    <div className="flex items-center gap-1.5 sm:gap-2 mt-auto pt-3 md:pt-4 border-t border-black/[0.06] dark:border-white/5 w-full">
                      <button
                        onClick={() => navigate('/products/medos')}
                        className={`flex-1 py-1.5 sm:py-2 md:py-2.5 rounded-full text-white font-bold text-[8px] sm:text-[9px] md:text-xs uppercase tracking-wider transition-all cursor-pointer border-none flex items-center justify-center hover:opacity-90 ${
                          plan.isPopular ? 'bg-blue-600' : 'bg-[#065DC0]'
                        }`}
                      >
                        {plan.ctaLabel}
                      </button>
                      <button
                        onClick={() => navigate('/products/medos')}
                        className={`flex-1 py-1.5 sm:py-2 md:py-2.5 rounded-full bg-transparent font-bold text-[8px] sm:text-[9px] md:text-xs uppercase tracking-wider transition-all border cursor-pointer flex items-center justify-center dark:hover:text-white ${
                          plan.isPopular ? 'border-blue-600 text-blue-600 hover:bg-blue-600' : 'border-[#065DC0] text-[#065DC0] hover:bg-[#065DC0]'
                        }`}
                      >
                        Learn more
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Comparison removed and merged into modern plans cards */}

      {/* MedOS Screenshot */}
      <section className="py-8 sm:py-12 md:py-16">
        <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative bg-white dark:bg-[#050505] rounded-[20px] sm:rounded-[32px] md:rounded-[44px] p-5 sm:p-8 md:p-14 overflow-hidden border border-black/[0.08] dark:border-white/[0.08] shadow-sm dark:shadow-[0_32px_64px_-16px_rgba(0,0,0,0.6)]"
          >
            {/* Creative Background Elements */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#065DC0]/10 blur-[120px] rounded-full -mr-64 -mt-64 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-600/5 blur-[100px] rounded-full -ml-40 -mb-40 pointer-events-none" />
            
            <div className="flex flex-col md:flex-row items-center gap-4 sm:gap-6 md:gap-8 relative z-10">
              <div className="flex-1">
                <h3 className="text-lg sm:text-2xl md:text-3xl font-bold tracking-tight mb-2 sm:mb-4 text-[#1d1d1f] dark:text-white">See MedOS in action.</h3>
                <p className="text-[#6e6e73] dark:text-[#a1a1a6] text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6">
                  Smart billing, intelligent inventory tracking, and seamless GST compliance.
                </p>
                <Link to="/products/medos" className="text-[#065DC0] text-xs sm:text-sm font-semibold inline-flex items-center gap-1 hover:underline">
                  Explore features <ArrowRight size={14} />
                </Link>
              </div>
              <div className="w-full sm:w-[280px] md:w-[400px] shrink-0 rounded-[16px] sm:rounded-[20px] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.1)] dark:shadow-none border border-black/[0.08] dark:border-white/[0.08]">
                <img
                  src={process.env.PUBLIC_URL + '/MEDOS Sales billing.png'}
                  alt="MedOS Billing"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-center text-[9px] sm:text-[10px] md:text-xs text-[#86868b] dark:text-[#6e6e73] px-4 py-8 sm:py-12 max-w-2xl mx-auto uppercase tracking-[0.1em]">
        By proceeding, you agree to FOZZIL's
        <a href="/terms" target="_blank" rel="noopener noreferrer" className="mx-1 sm:mx-2 text-[#6e6e73] dark:text-[#a1a1a6] hover:text-blue-500 transition-colors">Terms</a>
        &
        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="ml-1 sm:ml-2 text-[#6e6e73] dark:text-[#a1a1a6] hover:text-blue-500 transition-colors">Privacy</a>.
      </footer>
    </div>
  );
};

export default ShopMedos;
